import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Textarea,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FAMILY_APP_LIMITS } from '@twinden/shared';
import type { FamilyDocument } from '../../lib/api';
import { useT } from '../../i18n';

export interface DocumentChanges {
  description: string | null;
  folder: string | null;
  tags: string[];
}

/** "tax, #house  2026" → ["tax", "house", "2026"] */
export function parseTags(input: string): string[] {
  const tags = input
    .split(/[,\s]+/)
    .map((tag) => tag.trim().replace(/^#/, '').toLowerCase())
    .filter(Boolean)
    .map((tag) => tag.slice(0, FAMILY_APP_LIMITS.DOCUMENT_TAG_LENGTH));
  return [...new Set(tags)].slice(0, FAMILY_APP_LIMITS.DOCUMENT_TAGS);
}

// A document's folder, tags and note (M26, D49).
export function DocumentDetails({
  document,
  folders,
  isSaving,
  onSave,
  onClose,
}: {
  document: FamilyDocument | null;
  folders: string[];
  isSaving: boolean;
  onSave: (changes: DocumentChanges) => void;
  onClose: () => void;
}) {
  const t = useT();
  const sheet = useBreakpointValue({ base: true, md: false }) ?? true;
  const [folder, setFolder] = useState('');
  const [tags, setTags] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!document) return;
    setFolder(document.folder ?? '');
    setTags((document.tags ?? []).join(', '));
    setNote(document.description ?? '');
  }, [document]);

  const content = document && (
    <Stack spacing={5}>
      <Heading as="h2" fontSize="22px" noOfLines={2}>
        {document.fileName}
      </Heading>
      <FormControl>
        <FormLabel>{t('apps.documentDetails.folder')}</FormLabel>
        <Input
          value={folder}
          onChange={(event) => setFolder(event.target.value)}
          maxLength={FAMILY_APP_LIMITS.DOCUMENT_FOLDER_LENGTH}
          placeholder={t('apps.documentDetails.folderPlaceholder')}
          list="document-folders"
        />
        <datalist id="document-folders">
          {folders.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <FormHelperText>{t('apps.documentDetails.folderHint')}</FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel>{t('apps.documentDetails.tags')}</FormLabel>
        <Input
          value={tags}
          onChange={(event) => setTags(event.target.value)}
          placeholder={t('apps.documentDetails.tagsPlaceholder')}
        />
        <FormHelperText>{t('apps.documentDetails.tagsHint')}</FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel>{t('apps.documentDetails.note')}</FormLabel>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={FAMILY_APP_LIMITS.DOCUMENT_DESCRIPTION_LENGTH}
          rows={3}
          placeholder={t('apps.documentDetails.notePlaceholder')}
        />
      </FormControl>
      <Button
        h="52px"
        isLoading={isSaving}
        onClick={() =>
          onSave({
            folder: folder.trim() || null,
            tags: parseTags(tags),
            description: note.trim() || null,
          })
        }
      >
        {t('common.save')}
      </Button>
    </Stack>
  );

  if (sheet) {
    return (
      <Drawer isOpen={document !== null} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px">
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="gray.200"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Modal isOpen={document !== null} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent borderRadius="28px" bg="surface">
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
